import type { CareLang, CareSectionKey } from "./careTypes";

/** Consumer-facing labels. Keys never change; labels may. */
export const careTitle: Record<CareLang, string> = {
  en: "Care & Preservation",
  hi: "देखभाल और संरक्षण",
};

export const careSectionLabels: Record<CareSectionKey, Record<CareLang, string>> = {
  BEFORE_WEARING: { en: "Before wearing", hi: "पहनने से पहले" },
  AFTER_WEARING: { en: "After wearing", hi: "पहनने के बाद" },
  CLEANING: { en: "Cleaning", hi: "सफ़ाई" },
  STORAGE: { en: "Storage", hi: "रखरखाव" },
  STAIN_ACCIDENT: { en: "Stains & accidents", hi: "दाग़ या दुर्घटना" },
  LONG_TERM_PRESERVATION: { en: "Long-term preservation", hi: "लंबे समय तक सहेजना" },
};

export const careNotesLabel: Record<CareLang, string> = {
  en: "About this piece",
  hi: "इस कृति के बारे में",
};

export const careCopy = {
  pageTitle: "Care Templates",
  pageSubtitle: "Write care instructions once and attach them to your products. Buyers see them on the certificate.",
  newTemplate: "New template",
  myTemplates: "My templates",
  library: "HKP library",
  search: "Search templates",
  active: "Active",
  archived: "Archived",
  emptyMine: "No care templates yet. Start from the HKP library or write your own.",
  emptyLibrary: "The HKP library has no templates yet.",
  emptyArchived: "No archived templates.",
  libraryBadge: "HKP library",
  studioBadge: "Your studio",
  edit: "Edit",
  view: "View",
  duplicate: "Duplicate",
  duplicateHint: "Make your own copy to change the wording.",
  apply: "Apply to products",
  archive: "Archive",
  restore: "Restore",
  name: "Template name",
  namePlaceholder: "e.g. Banarasi silk with zari",
  lineHint: "One instruction per line. About 4–6 short lines per section.",
  english: "English",
  hindi: "हिंदी",
  save: "Save template",
  saving: "Saving…",
  cancel: "Cancel",
  discardTitle: "Discard changes?",
  discardBody: "Your edits to this template have not been saved.",
  discard: "Discard",
  archiveTitle: "Archive this template?",
  archiveBody:
    "It will no longer be offered for new products. Products already using it keep their care exactly as it is.",
  conflict: "Someone else changed this. Reload to see the latest, then make your change again.",
  reload: "Reload",
  sectionsFilled: (n: number) => `${n} of 6 sections`,
  usedOn: (n: number) => (n === 1 ? "used on 1 product" : `used on ${n} products`),
  libraryReadOnly: "HKP library templates can't be edited here. Duplicate it to make your own copy.",
  preview: "Preview",
};

export const productCareCopy = {
  title: "Care & Preservation",
  notSet: "No care instructions yet. Buyers see care on the certificate once you add it.",
  add: "Add care",
  chooseTemplate: "Care template",
  choosePlaceholder: "Choose a template",
  noTemplates: "No care templates yet.",
  manageTemplates: "Manage care templates",
  fromTemplate: (name: string) => `From “${name}”`,
  updateAvailable: "This template was updated. Apply the new wording to this product?",
  applyLatest: "Apply latest",
  archivedNote: "This template is archived. The product keeps its care; choose another template to change it.",
  edit: "Edit for this product",
  changeTemplate: "Change template",
  print: "Print care card",
  remove: "Remove care",
  removeTitle: "Remove care from this product?",
  removeBody: "Buyers will no longer see care instructions on this product's certificate.",
  save: "Save care",
  saving: "Saving…",
  cancel: "Cancel",
  customise: "Change for this product",
  useTemplate: "Use template text",
  hide: "Hide this section",
  hidden: "Hidden on this product",
  notes: "Notes about this piece (optional)",
  keepChanges: "Keep my changes for this product",
  clearChanges: "Use the new template as it is",
  changesQuestion: "This product has its own changes. When switching template:",
  saved: "Care saved",
  conflict: "This product's care was changed elsewhere. Reload to see the latest.",
  templateChanged: "The template was edited while you were choosing. Its preview has been refreshed; check it and save again.",
  empty: "This template has no text yet. Add text to the template first.",
};
